import { getRegionCode, isNumeric, isString } from '@yembo/yemblocks-core/dist/_util';
import { snakeCase } from 'change-case';
import { CountryCode, parsePhoneNumberWithError } from 'libphonenumber-js';
import { getUnits } from 'src/react/_util/getUnits';
import { SystemOfUnits, Unit, UnitCategory } from 'src/types';

export const getOrdinalNumber = (num: number) => {
  if (num === 1) return '';

  const suffix = ['th', 'st', 'nd', 'rd'];
  const value = num % 100;

  return `${num}${suffix[(value - 20) % 10] || suffix[value] || suffix[0]}`;
};

export const appendCredentialsToForm = ({
  form,
  formInputs,
}: {
  form: FormData;
  formInputs: Record<string, string>;
}) => {
  Object.entries(formInputs).forEach(([key, value]) => form.append(key, value));
};

export const appendFileToForm = ({ form, file, key }: { form: FormData; file: Blob; key: string }) => {
  form.set('key', key);
  form.set('Content-Type', file.type);
  form.append('file', file);
};

export const toNationalPhoneNumber = ({ phone, defaultCountry }: { phone: string; defaultCountry?: string }) => {
  try {
    const regionCode = getRegionCode({ phone, country: defaultCountry });
    const parsedPhoneNumber = parsePhoneNumberWithError(
      phone,
      (regionCode ?? defaultCountry) as CountryCode | undefined
    );
    return parsedPhoneNumber.formatNational();
  } catch {
    return phone;
  }
};

export const isoToMilliseconds = (isoTimestamp: string) => new Date(isoTimestamp).getTime();

export const trimString = (str: string) => str.replace(/^\s+|\s+$/g, '').replace(/\s\s+/g, ' ');

export const toComparableString = (value?: string | null) => value?.trim().toLowerCase() ?? '';

export const formatFloat = (value: string | number, decimalCount = 2) => {
  let shouldNotFormat = false;

  if (isString(value)) {
    const hasOneDecimal = (value.match(/\./g) || []).length === 1;
    const endsWithDecimal = value.endsWith('.');
    const isDecimalInProgress = hasOneDecimal && endsWithDecimal;
    const isEmptyString = value === '';

    if (endsWithDecimal && !hasOneDecimal) return value.replace('.', '');

    shouldNotFormat = isDecimalInProgress || isEmptyString;
  }

  return shouldNotFormat
    ? trimString(value.toString())
    : Number(parseFloat(value.toString()).toFixed(decimalCount)).toString();
};

export const isMetric = (unitPreference: SystemOfUnits) => unitPreference === 'metric';

export const formatWithUnit = ({
  key,
  value,
  imperialUnit,
  metricUnit,
  unitPreference,
  canRoundValue = true,
}: {
  key: string;
  value: string | number | null | undefined;
  imperialUnit: string;
  metricUnit: string;
  unitPreference: SystemOfUnits;
  canRoundValue?: boolean;
}) => {
  if (value) {
    const convertedNumber = Number(formatFloat(withUnitConversion({ sendingTo: 'UI', key, value, unitPreference })));
    const unit = isMetric(unitPreference) ? metricUnit : imperialUnit;
    const showWholeNumber = canRoundValue && Boolean(convertedNumber > 10);
    const shouldShow = convertedNumber > 0;

    const result = (showWholeNumber ? Math.round(convertedNumber) : convertedNumber).toLocaleString();
    return shouldShow ? `${result} ${unit}` : null;
  }
};

export const convertPoundsToKilograms = (pounds: string | number, precision?: number): number => {
  const kilograms = +pounds / 2.20462;
  return precision !== undefined ? +round({ value: kilograms, precision }) : kilograms;
};

export const convertKilogramsToPounds = (kilograms: string | number, precision?: number): number => {
  const pounds = +kilograms * 2.20462;
  return precision !== undefined ? +round({ value: pounds, precision }) : pounds;
};

export const dimensionWithUnitConversion = (
  value: string | number,
  sendingTo: 'UI' | 'API',
  unitPreference: SystemOfUnits
) =>
  withUnitConversion({
    key: 'dimension',
    value,
    unitPreference,
    sendingTo,
    round: sendingTo === 'API' ? 3 : 2,
  }).replace(/\.?0+$/, '');

export const withUnitConversion = ({
  key,
  value = '',
  sendingTo,
  unitPreference,
  round,
}: {
  key: string;
  value: string | number | null | undefined;
  sendingTo: 'UI' | 'API';
  unitPreference: SystemOfUnits;
  round?: 'whole' | number;
}) => {
  const toMetric = sendingTo === 'UI';

  const isMetricSystem = unitPreference === 'metric';

  if (!value) return '0';

  if (!isMetricSystem)
    return round ? parseFloat(value.toString()).toFixed(round === 'whole' ? undefined : round) : value.toString();

  const numberValue = Number(value);
  let convertedValue: number;

  switch (key) {
    case 'weight':
    case 'totalWeight':
      convertedValue = toMetric ? convertPoundsToKilograms(numberValue) : convertKilogramsToPounds(numberValue);
      break;

    case 'area':
    case 'originArea':
      convertedValue = toMetric ? numberValue / 10.764 : numberValue * 10.764;
      break;
    case 'distance':
      // 'miles' to 'km'
      convertedValue = toMetric ? numberValue * 1.609344 : numberValue / 1.609344;
      break;
    case 'dimension':
      // inch to cm
      convertedValue = toMetric ? numberValue * 2.54 : numberValue / 2.54;
      break;
    default:
      return value.toString();
  }

  return round ? convertedValue.toFixed(round === 'whole' ? undefined : round) : convertedValue.toString();
};

export const roundToWholeNumber = (value: unknown, isNumberWithCommas = false) => {
  if (isNumeric(value)) {
    const validValue = Math.round(Number(value)).toString();

    return isNumberWithCommas ? validValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : validValue;
  } else {
    return '0';
  }
};

export const capitalize = (value: string) => (isString(value) ? value.charAt(0).toUpperCase() + value.slice(1) : null);

export const capitalizeWords = (string: string, separator = ' ', joinBy = ' '): string => {
  if (!string) return '';

  return string
    .split(separator)
    .map((word) => `${word.charAt(0).toLocaleUpperCase()}${word.slice(1)}`)
    .join(joinBy);
};

export const kebabCase = (value: string) => snakeCase(value).replace(/_/g, '-');

export const formatForDisplay = ({
  unitKey,
  value,
  unitPreference,
  isTotal = false,
  isLocalFormat = false,
  convertImperialToUnitPreference = true,
  unitAdornment = '',
}: {
  unitKey?: UnitCategory;
  value: string | number | null | undefined;
  unitPreference: SystemOfUnits;
  isTotal?: boolean;
  unitAdornment?: string;
  convertImperialToUnitPreference?: boolean;
  isLocalFormat?: boolean;
}): string => {
  if (!value) return '0';

  value = parseFloat(value.toString());

  const unit = getUnit({
    unitKey,
    unitPreference,
  });

  let formattedValue = (() => {
    const precision = isTotal ? precisionsForTotals(unit) : null;

    switch (unit) {
      case 'ea':
      case 'in':
        return round({
          value,
          precision: precision ?? 0,
        });
      case 'lb':
        return round({
          value,
          precision: precision ?? 1,
        });
      case 'cm': //'in' to 'cm'
        return round({
          value: convertImperialToUnitPreference ? value * 2.54 : value,
          precision: precision ?? 0,
        });
      case 'kg': //'lb' to 'kg'
        return round({
          value: convertImperialToUnitPreference ? value * 0.4536 : value,
          precision: precision ?? 1,
        });

      case 'km': //'mi' to 'km'
        return round({
          value: convertImperialToUnitPreference ? value * 1.609 : value,
          precision: precision ?? 1,
        });
      default:
        return value.toString();
    }
  })();

  formattedValue = parseFloat(formattedValue).toString();

  if (isLocalFormat) {
    formattedValue = parseFloat(formattedValue).toLocaleString();
  }

  const isUnitOfMeasurement = Boolean(unitAdornment);
  if (isUnitOfMeasurement) {
    formattedValue = formattedValue.concat(` ${unitAdornment}`);
  }

  return formattedValue;
};

export const formatForApi = ({
  unitKey,
  value,
  unitPreference,
  isTotal = false,
}: {
  unitKey?: UnitCategory;
  value: string | number | null | undefined;
  unitPreference: SystemOfUnits;
  isTotal?: boolean;
}): number => {
  if (!value) return 0;

  value = parseFloat(value.toString());
  const unit = getUnit({
    unitKey,
    unitPreference,
  });

  const formattedValue = (() => {
    const precision = isTotal ? precisionsForTotals(unit) : null;

    switch (unit) {
      case 'ea':
      case 'in':
        return round({
          value,
          precision: precision ?? 0,
        });
      case 'lb':
        return round({
          value,
          precision: precision ?? 1,
        });
      case 'cm': //cm' to 'in'
        return round({
          value: value / 2.54,
          precision: precision ?? 1,
        });
      case 'kg': //'kg' to 'lb'
        return round({
          value: value / 0.4536,
          precision: precision ?? 1,
        });

      default:
        return value.toString();
    }
  })();

  return Number(formattedValue);
};

function getUnit({
  unitPreference,
  unitKey,
}: {
  unitPreference: SystemOfUnits;
  unitKey: UnitCategory | undefined;
}): Unit {
  const units = getUnits(unitPreference);
  const unit = (() => {
    switch (unitKey) {
      case 'weight':
        return units.weightUnit;

      case 'distance':
        return units.distanceUnit;
      case 'dimension':
        return units.dimensionUnit;
      case 'quantity':
        return units.quantityUnit;
      default:
        return 'ea';
    }
  })();

  return unit;
}

function round({ value, precision }: { value: number; precision: number }) {
  const precisionFactor = Math.pow(10, precision);

  return (Math.round(value * precisionFactor) / precisionFactor).toFixed(precision);
}

function precisionsForTotals(unit: Unit) {
  if (['ea', 'in', 'lb', 'cm', 'kg'].includes(unit)) return 0;

  if (['mi', 'km'].includes(unit)) return 1;

  return 0;
}
